import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { IPhase, IProject, PhaseModel, PhaseType } from '../types';
import { createPhaseModel } from '../utils/phaseFactory';
import { queries, mutations } from '../../../graphql/graphql';

interface ProjectContextValue {
  project: IProject | null;
  loading: boolean;
  saving: boolean;
  activePhase: IPhase | null;
  phaseModels: Record<string, PhaseModel>;
  setActivePhase: (phaseId: string) => void;
  persistActivePhase: (phaseId: string, patch?: Partial<IPhase>) => Promise<void>;
  updatePhase: (
    phaseId: string,
    patch: Partial<IPhase> | ((phase: IPhase) => Partial<IPhase>)
  ) => void;
  savePhase: (
    phaseId: string,
    patch?: Partial<IPhase> | ((phase: IPhase) => Partial<IPhase>),
    options?: SavePhaseOptions
  ) => Promise<IPhase | null>;
  saveProject: (update?: Partial<IProject>) => Promise<void>;
  loadProject: () => Promise<void>;
  getPhaseById: (phaseId: string) => IPhase | null;
  getPhaseByType: (phaseType: PhaseType) => IPhase | null;
  getNextPhase: (phaseId: string) => IPhase | null;
  getPreviousPhase: (phaseId: string) => IPhase | null;
}

export type SavePhaseOptions = {
  omitIsActive?: boolean;
};

const ProjectContext = React.createContext<ProjectContextValue | undefined>(undefined);

interface ProjectProviderProps {
  children: React.ReactNode;
  projectId?: string;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children, projectId }) => {
  const [project, setProject] = React.useState<IProject | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const phaseModelsRef = React.useRef<Record<string, PhaseModel>>({});
  const projectRef = React.useRef<IProject | null>(null);
  const savePhaseRef = React.useRef<ProjectContextValue['savePhase']>(null!);
  const {
    data: projectData,
    loading: projectLoading,
    refetch: refetchProject,
    error: projectError,
  } = useQuery(queries.project.PROJECT, {
    fetchPolicy: 'network-only',
    variables: { id: projectId },
    skip: !projectId,
    onError: err => console.error('[ProjectProvider] failed to load project', err),
  });

  const {
    data: myProjectsData,
    loading: myProjectsLoading,
    refetch: refetchMyProjects,
    error: myProjectsError,
  } = useQuery(queries.project.MY_PROJECTS, {
    fetchPolicy: 'network-only',
    skip: !!projectId,
    onError: err => console.error('[ProjectProvider] failed to load projects', err),
  });

  const activeData = projectId ? projectData : myProjectsData;
  const activeLoading = projectId ? projectLoading : myProjectsLoading;
  const activeError = projectId ? projectError : myProjectsError;
  const activeRefetch = projectId ? refetchProject : refetchMyProjects;

  React.useEffect(() => {
    setLoading(activeLoading);
  }, [activeLoading]);

  React.useEffect(() => {
    if (activeError) {
      console.error('[ProjectProvider] query error', activeError);
    }
  }, [activeError]);

  React.useEffect(() => {
    if (!activeData) {
      return;
    }

    const nextProject = projectId
      ? (activeData?.project as IProject | null) || null
      : (activeData?.myProjects as IProject[])[0] || null;
    setProject(nextProject);
    projectRef.current = nextProject;

    if (!nextProject) {
      phaseModelsRef.current = {};
      return;
    }

    const phases = nextProject.phases || [];
    const models: Record<string, PhaseModel> = {};
    phases.forEach(phase => {
      models[phase.id] = createPhaseModel(phase);
    });
    phaseModelsRef.current = models;
    Object.values(phaseModelsRef.current).forEach(model => model?.load());
  }, [activeData, projectId]);

  React.useEffect(() => {
    projectRef.current = project;
  }, [project]);

  React.useEffect(() => {
    if (!projectId) return;
    if (projectRef.current?.id === projectId) return;
    setProject(null);
    phaseModelsRef.current = {};
  }, [projectId]);

  const [updateProjectMutation] = useMutation(mutations.project.UPDATE_PROJECT);
  const [updatePhaseMutation] = useMutation(mutations.project.UPDATE_PHASE);

  // Build a strict PhaseUpdateInput payload so we persist only supported fields.
  const buildPhaseInput = React.useCallback((phase: IPhase, options?: SavePhaseOptions) => {
    const input = {
      name: phase.name,
      type: phase.type,
      progress: phase.progress,
      sequence: phase.sequence,
      locked: phase.locked,
      status: phase.status,
      users: phase.users?.map(user => ({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
      })),
      data: phase.data,
      notes: phase.notes?.map(note => ({
        id: note.id,
        text: note.text,
        createdAt: note.createdAt,
        authorId: note.authorId,
      })),
      isHidden: phase.isHidden,
      isActive: phase.isActive,
      meta: phase.meta,
    };
    if (options?.omitIsActive) {
      delete input.isActive;
    }
    return input;
  }, []);

  // Build a ProjectUpdateInput payload for partial project saves.
  const buildProjectInput = React.useCallback((projectUpdate: Partial<IProject>) => {
    return {
      name: projectUpdate.name,
      status: projectUpdate.status,
      meta: projectUpdate.meta,
      users: projectUpdate.users?.map(user => ({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
      })),
    };
  }, []);

  // Refetch the current project (or fallback list) to refresh local cache.
  const loadProject = React.useCallback(async () => {
    setLoading(true);
    try {
      if (!activeRefetch) {
        console.warn('[ProjectProvider] loadProject called without active refetch');
        return;
      }
      await activeRefetch();
    } catch (err) {
      console.error('[ProjectProvider] failed to refetch projects', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeRefetch, projectId]);

  // Persist project-level fields only; phases are saved separately.
  const saveProject = React.useCallback(
    async (update?: Partial<IProject>) => {
      setSaving(true);
      try {
        const currentProject = projectRef.current;
        const mergedProject = currentProject
          ? { ...currentProject, ...(update || {}) }
          : currentProject;

        if (!mergedProject) {
          console.warn('[ProjectProvider] saveProject called without a loaded project');
          return;
        }

        if (update) {
          setProject(mergedProject);
          projectRef.current = mergedProject;
        }

        if (mergedProject && update) {
          const projectInput = buildProjectInput(update);
          const hasProjectChanges = Object.values(projectInput).some(value => value !== undefined);
          if (hasProjectChanges) {
            await updateProjectMutation({
              variables: { id: mergedProject.id, input: projectInput },
            });
          }
        }
      } finally {
        setSaving(false);
      }
    },
    [buildProjectInput, updateProjectMutation]
  );

  // Lookup a phase by id from the latest project snapshot.
  const getPhaseById = React.useCallback((phaseId: string) => {
    if (!phaseId) return null;
    return projectRef.current?.phases.find(phase => phase.id === phaseId) || null;
  }, []);

  // Lookup a phase by type for convenience in UI flows.
  const getPhaseByType = React.useCallback((phaseType: PhaseType) => {
    return projectRef.current?.phases.find(phase => phase.type === phaseType) || null;
  }, []);

  // Provide a stable sequence order for prev/next navigation.
  const getSortedPhases = React.useCallback(() => {
    const phases = projectRef.current?.phases || [];
    return [...phases].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  }, []);

  // Get the next phase in sequence for linear flows.
  const getNextPhase = React.useCallback(
    (phaseId: string) => {
      const phases = getSortedPhases();
      const index = phases.findIndex(phase => phase.id === phaseId);
      if (index < 0) return null;
      return phases[index + 1] || null;
    },
    [getSortedPhases]
  );

  // Get the previous phase in sequence for linear flows.
  const getPreviousPhase = React.useCallback(
    (phaseId: string) => {
      const phases = getSortedPhases();
      const index = phases.findIndex(phase => phase.id === phaseId);
      if (index <= 0) return null;
      return phases[index - 1] || null;
    },
    [getSortedPhases]
  );

  // Switch the locally viewed phase without persisting to the server.
  const setActivePhase = React.useCallback((phaseId: string) => {
    if (!phaseId) return;
    setProject(prev => {
      if (!prev) return prev;

      const previousActive = prev.phases.find(phase => phase.isActive && phase.id !== phaseId);
      const updatedPhases = prev.phases.map(phase => {
        if (phase.id === phaseId) {
          return { ...phase, isActive: true };
        }
        if (previousActive && phase.id === previousActive.id) {
          return { ...phase, isActive: false };
        }
        return phase;
      });

      const updatedProject = { ...prev, phases: updatedPhases };
      projectRef.current = updatedProject;

      const activePhase = updatedPhases.find(phase => phase.id === phaseId);
      if (activePhase) {
        phaseModelsRef.current[activePhase.id]?.sync(activePhase);
      }
      if (previousActive) {
        const updatedPrevious = updatedPhases.find(phase => phase.id === previousActive.id);
        if (updatedPrevious) {
          phaseModelsRef.current[updatedPrevious.id]?.sync(updatedPrevious);
        }
      }

      return updatedProject;
    });
  }, []);

  // Persist a phase transition (deactivate previous, activate next) to the server.
  const persistActivePhase = React.useCallback(
    async (phaseId: string, patch?: Partial<IPhase>) => {
      if (!phaseId) return;
      try {
        const currentProject = projectRef.current;
        if (!currentProject) return;

        const currentActive = currentProject.phases.find(
          phase => phase.isActive && phase.id !== phaseId
        );
        const targetPhase = currentProject.phases.find(phase => phase.id === phaseId);
        if (!targetPhase) return;

        setActivePhase(phaseId);

        if (currentActive) {
          const nextStatus =
            currentActive.status === 'active' ? ('open' as const) : currentActive.status;
          await savePhaseRef.current(currentActive.id, { isActive: false, status: nextStatus });
        }

        const nextPatch = { ...(patch || {}), isActive: true, status: 'active' as const };
        await savePhaseRef.current(phaseId, nextPatch);
      } catch (error) {
        console.error('[ProjectProvider] failed to persist active phase', error);
      }
    },
    [setActivePhase]
  );

  // Apply a local patch to a phase and keep its model in sync.
  const updatePhase = React.useCallback(
    (phaseId: string, patch: Partial<IPhase> | ((phase: IPhase) => Partial<IPhase>)) => {
      setProject(prev => {
        if (!prev) return prev;

        const updatedPhases = prev.phases.map(phase => {
          if (phase.id !== phaseId) return phase;

          const patchValue = typeof patch === 'function' ? patch(phase) : patch;
          const nextPhase = { ...phase, ...patchValue };
          const model = phaseModelsRef.current[phase.id];

          if (model) {
            model.sync(nextPhase);
            if (patchValue?.data !== undefined) {
              model.updateData(patchValue.data as never);
            }
          }

          return nextPhase;
        });

        const updatedProject = { ...prev, phases: updatedPhases };
        projectRef.current = updatedProject;
        return updatedProject;
      });
    },
    []
  );

  // Persist a single phase to the server, keeping local state consistent.
  const savePhase = React.useCallback(
    async (
      phaseId: string,
      patch?: Partial<IPhase> | ((phase: IPhase) => Partial<IPhase>),
      options?: SavePhaseOptions
    ) => {
      if (!phaseId) return null;
      setSaving(true);
      try {
        const currentPhase = getPhaseById(phaseId);
        if (!currentPhase) {
          console.warn('[ProjectProvider] savePhase called without a matching phase', phaseId);
          return null;
        }

        const patchValue = patch
          ? typeof patch === 'function'
            ? patch(currentPhase)
            : patch
          : undefined;
        const nextPhase = patchValue ? { ...currentPhase, ...patchValue } : currentPhase;

        if (patchValue) {
          updatePhase(phaseId, patchValue);
        }

        const model = phaseModelsRef.current[phaseId];
        if (model) {
          await model.save();
        }

        const input = buildPhaseInput(nextPhase, options);
        const res = await updatePhaseMutation({
          variables: { id: phaseId, input },
        });
        const persistedPhase = res.data?.updatePhase ?? nextPhase;

        setProject(prev => {
          if (!prev) return prev;

          const updatedPhases = prev.phases.map(phase =>
            phase.id === phaseId ? persistedPhase : phase
          );
          const updatedProject = { ...prev, phases: updatedPhases };
          projectRef.current = updatedProject;
          return updatedProject;
        });

        phaseModelsRef.current[phaseId]?.sync(persistedPhase);

        return persistedPhase;
      } finally {
        setSaving(false);
      }
    },
    [buildPhaseInput, getPhaseById, updatePhase, updatePhaseMutation]
  );

  // Keep ref in sync so persistActivePhase always calls the latest savePhase.
  savePhaseRef.current = savePhase;

  const activePhase = React.useMemo(() => {
    if (!project) return null;

    return project.phases.find(phase => phase.isActive) || project.phases[0] || null;
  }, [project]);

  const value: ProjectContextValue = React.useMemo(
    () => ({
      project,
      loading,
      saving,
      activePhase,
      phaseModels: phaseModelsRef.current,
      setActivePhase,
      persistActivePhase,
      updatePhase,
      savePhase,
      saveProject,
      loadProject,
      getPhaseById,
      getPhaseByType,
      getNextPhase,
      getPreviousPhase,
    }),
    [
      activePhase,
      project,
      loading,
      saving,
      setActivePhase,
      persistActivePhase,
      updatePhase,
      savePhase,
      saveProject,
      loadProject,
      getPhaseById,
      getPhaseByType,
      getNextPhase,
      getPreviousPhase,
    ]
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProjectContext = (): ProjectContextValue => {
  const ctx = React.useContext(ProjectContext);

  if (!ctx) {
    throw new Error('useProjectContext must be used within ProjectProvider');
  }

  return ctx;
};
